import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useTicketingIntegrations from "../../../../api/hooks/useTicketingIntegrations";
import {
  asanaTicketKeys,
  jiraIssuesKeys,
  serviceNowTicketKeys,
  ticketKeys,
} from "../../../../api/queryKeys";
import { Toast } from "../../../../components/Toast";
import { getControlEntityIds } from "../../../posture/controls/components/EntitiesSection/utils";
import IssueLinkingModal from "../Jira/IssueLinkingModal";
import ServiceNowButton from "./ServiceNowButton";
import ServiceNowIncidentCreationModal from "./ServiceNowIncidentCreationModal";

export default function ServiceNowInteractionManager({
  fullControl,
  sx,
  hideTitle = false,
}) {
  const queryClient = useQueryClient();
  const {
    createServiceNowIncident,
    trackEvent,
    getTicketByControlID,
    getJiraIssueByControlID,
    getServiceNowIncidentByControlID,
  } = useAPI();

  const [
    isServiceNowIncidentCreationModalOpen,
    setIsServiceNowIncidentCreationModalOpen,
  ] = useState(false);
  const [
    isServiceNowIncidentLinkingModalOpen,
    setIsServiceNowIncidentLinkingModalOpen,
  ] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    isJiraIntegrated,
    isAsanaIntegrated,
    isServiceNowIntegrated,
    isLoading: isLoadingIntegrations,
  } = useTicketingIntegrations();

  const { data: serviceNowIncident, isLoading: isLoadingServiceNowIncident } =
    useQuery({
      queryKey: serviceNowTicketKeys.serviceNowTicketControl(
        fullControl?.control_id
      ),
      queryFn: async () => {
        const incident = (
          await getServiceNowIncidentByControlID(fullControl?.control_id)
        ).data;
        return incident;
      },
      enabled:
        fullControl !== null &&
        isServiceNowIntegrated &&
        !isLoadingIntegrations,
    });

  const { data: jiraIssue, isLoading: isLoadingJiraIssue } = useQuery({
    queryKey: jiraIssuesKeys.jiraIssueControl(fullControl?.control_id),
    queryFn: async () => {
      const issue = (await getJiraIssueByControlID(fullControl?.control_id))
        .data;
      return issue;
    },
    enabled: fullControl !== null && isJiraIntegrated && !isLoadingIntegrations,
  });

  const { data: asanaTicket, isLoading: isLoadingAsanaTicket } = useQuery({
    queryKey: asanaTicketKeys.asanaTicketControl(fullControl?.control_id),
    queryFn: async () => {
      const ticket = (await getTicketByControlID(fullControl?.control_id)).data;
      return ticket;
    },
    enabled:
      fullControl !== null && isAsanaIntegrated && !isLoadingIntegrations,
  });

  const createServiceNowIncidentFn = () => {
    setIsServiceNowIncidentCreationModalOpen(true);
    trackEvent("User opened ServiceNow incident creation modal").then();
  };

  const handleCloseServiceNowIncidentCreationModal = () => {
    setIsServiceNowIncidentCreationModalOpen(false);
    trackEvent("User closed ServiceNow incident creation modal").then();
  };

  const handleCreateIncident = async (incidentData) => {
    setIsLoading(true);

    const incidentContent = {
      short_description: incidentData.shortDescription,
      description: incidentData.description,
      urgency: incidentData.urgency,
      category: incidentData.category,
      entity_ids: incidentData.includeEntities
        ? getControlEntityIds(fullControl)
        : [],
      link_to_gap: fullControl?.control_id
        ? `${window.location.origin}/posture/controls?control_id=${fullControl?.control_id}`
        : "",
    };

    try {
      const res = await createServiceNowIncident(incidentContent);
      await trackEvent("User created ServiceNow incident");
      Toast.success({
        text: "ServiceNow incident created successfully!",
        recommended_action: "View",
        recommended_action_callback: () => {
          const incidentUrl = res?.data?.url;
          if (incidentUrl) {
            window.open(incidentUrl, "_blank");
          }
        },
      });
      queryClient.invalidateQueries({
        queryKey: serviceNowTicketKeys.serviceNowTicketControl(
          fullControl?.control_id
        ),
      });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      handleCloseServiceNowIncidentCreationModal();
    } catch (error) {
      Toast.error(
        "Failed to create ServiceNow incident. Please try again or contact support."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const linkServiceNowIncident = () => {
    setIsServiceNowIncidentLinkingModalOpen(true);
    trackEvent("User opened ServiceNow incident linking modal").then();
  };

  const handleCloseServiceNowIncidentLinkingModal = () => {
    setIsServiceNowIncidentLinkingModalOpen(false);
    trackEvent("User closed ServiceNow incident linking modal").then();
  };

  return (
    <>
      {/* SERVICENOW BUTTON */}
      {isServiceNowIntegrated && (
        <ServiceNowButton
          fullControl={fullControl}
          createServiceNowIncident={createServiceNowIncidentFn}
          linkServiceNowIncident={linkServiceNowIncident}
          serviceNowIncidentLink={serviceNowIncident?.source || null}
          loading={
            isLoadingIntegrations ||
            isLoadingServiceNowIncident ||
            isLoadingJiraIssue ||
            isLoadingAsanaTicket
          }
          sx={sx}
          hideTitle={hideTitle}
          disabled={
            !!jiraIssue?.source || !!asanaTicket?.asana_details?.task_link
          }
        />
      )}

      {/* SERVICENOW CREATION MODAL */}
      {isServiceNowIncidentCreationModalOpen && (
        <ServiceNowIncidentCreationModal
          open={isServiceNowIncidentCreationModalOpen}
          onClose={handleCloseServiceNowIncidentCreationModal}
          onSubmit={handleCreateIncident}
          isLoading={isLoading}
          fullControl={fullControl}
        />
      )}

      {/* SERVICENOW LINK EXISTING INCIDENT MODAL */}
      {isServiceNowIncidentLinkingModalOpen && (
        <IssueLinkingModal
          open={isServiceNowIncidentLinkingModalOpen}
          onClose={handleCloseServiceNowIncidentLinkingModal}
          gapId={fullControl?.control_id}
          tool="ServiceNow"
          ticketType="incident"
        />
      )}
    </>
  );
}
